import { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  CalendarClock,
  ChevronDown,
  CreditCard,
  Eye,
  Globe,
  LayoutGrid,
  LogOut,
  Menu,
  Printer,
  QrCode,
  Receipt,
  ScrollText,
  Settings,
  ShieldCheck,
  Store,
  Tags,
  UtensilsCrossed,
  Users,
} from 'lucide-react';
import { Button, Spinner } from '@adop/ui';
import { useAuth } from '../lib/auth-context';
import { useWorkspace } from '../lib/workspace-context';
import { isActiveLocation } from '../lib/workspace-selection';
import { fetchAdminMe } from '../lib/admin-api';
import { useAsync } from '../lib/useAsync';
import { Brand } from './Brand';
import { RoleBadge } from './StatusBadge';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Store;
  ownerOnly?: boolean;
  /** Only owner/manager; staff do not have table or visibility access. */
  manageOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: '/dashboard', label: 'Home', icon: Store },
  { to: '/dashboard/orders', label: 'Orders', icon: UtensilsCrossed },
  { to: '/dashboard/table-board', label: 'Table board', icon: LayoutGrid },
  { to: '/dashboard/tables', label: 'Tables', icon: QrCode, manageOnly: true },
  { to: '/dashboard/reservations', label: 'Reservations', icon: CalendarClock },
  { to: '/dashboard/menu', label: 'Menu', icon: Tags },
  { to: '/dashboard/hours', label: 'Hours', icon: CalendarClock },
  { to: '/dashboard/visibility', label: 'Visibility', icon: Eye, manageOnly: true },
  { to: '/dashboard/branding', label: 'Branding & site', icon: Globe },
  { to: '/dashboard/payments', label: 'Ordering & payments', icon: CreditCard },
  { to: '/dashboard/billing', label: 'Billing', icon: Receipt, manageOnly: true },
  { to: '/dashboard/reservation-settings', label: 'Reservation settings', icon: Settings },
  { to: '/dashboard/domain', label: 'Domain', icon: Globe },
  { to: '/dashboard/printers', label: 'Printers', icon: Printer },
  { to: '/dashboard/locations', label: 'Locations', icon: Store },
  { to: '/dashboard/team', label: 'Team', icon: Users },
  { to: '/dashboard/stats', label: 'Stats', icon: BarChart3 },
  { to: '/dashboard/activity', label: 'Activity', icon: ScrollText, ownerOnly: true },
];

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
    isActive ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
  }`;
}

export function DashboardLayout() {
  const { user, profile, memberships, loading, signOut } = useAuth();
  const workspace = useWorkspace();
  const navigate = useNavigate();
  const admin = useAsync(fetchAdminMe, []);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (memberships.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-zinc-50 px-4 text-center">
        <h1 className="text-xl font-semibold text-zinc-900">No restaurant yet</h1>
        <p className="max-w-sm text-sm text-zinc-500">
          Your account is not connected to a restaurant. Register one to open the dashboard, or ask for an
          invitation.
        </p>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/register')}>Register a restaurant</Button>
          <Button variant="outline" onClick={() => signOut()}>
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  const nav = (
    <nav className="flex flex-col gap-1">
      {NAV.filter((item) => (!item.ownerOnly || workspace.canOwn) && (!item.manageOnly || workspace.canManage)).map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/dashboard'}
            className={navLinkClass}
            onClick={() => setSidebarOpen(false)}
          >
            <Icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="sticky top-0 z-30 border-b border-zinc-200 bg-white">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            aria-label="Toggle navigation"
            onClick={() => setSidebarOpen((value) => !value)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <Brand to="/dashboard" className="mr-2" />

          <div className="flex flex-1 flex-wrap items-center gap-2">
            <select
              aria-label="Restaurant"
              value={workspace.restaurant?.id ?? ''}
              onChange={(event) => workspace.setRestaurantId(event.target.value)}
              className="max-w-[12rem] rounded-lg border border-zinc-200 bg-white px-2 py-1.5 text-sm"
            >
              {workspace.restaurants.map((entry) => (
                <option key={entry.restaurant.id} value={entry.restaurant.id}>
                  {entry.restaurant.name}
                </option>
              ))}
            </select>
            <select
              aria-label="Location"
              value={workspace.location?.id ?? ''}
              onChange={(event) => workspace.setLocationId(event.target.value)}
              className="max-w-[12rem] rounded-lg border border-zinc-200 bg-white px-2 py-1.5 text-sm"
            >
              {workspace.visibleLocations.filter(isActiveLocation).map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
            {workspace.role && workspace.restaurant ? <RoleBadge role={workspace.role} /> : null}
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => setUserMenuOpen((value) => !value)}
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-zinc-100"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-zinc-900 text-xs font-semibold text-white">
                {(profile?.full_name ?? user?.email ?? '?').charAt(0).toUpperCase()}
              </span>
              <ChevronDown className="h-4 w-4 text-zinc-500" />
            </button>
            {userMenuOpen ? (
              <div className="absolute right-0 mt-1 w-56 rounded-lg border border-zinc-200 bg-white p-2 shadow-lg">
                <p className="px-2 py-1 text-xs text-zinc-500">{user?.email}</p>
                {admin.data?.is_admin ? (
                  <Link
                    to="/admin"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-zinc-700 hover:bg-zinc-100"
                  >
                    <ShieldCheck className="h-4 w-4" /> Platform admin
                  </Link>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setUserMenuOpen(false);
                    void signOut();
                  }}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-zinc-700 hover:bg-zinc-100"
                >
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl">
        <aside className="hidden w-60 shrink-0 border-r border-zinc-200 bg-white px-3 py-4 lg:block">
          {nav}
        </aside>
        {sidebarOpen ? (
          <div className="fixed inset-0 z-20 lg:hidden" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-zinc-900/40" onClick={() => setSidebarOpen(false)} />
            <div className="absolute left-0 top-0 h-full w-64 bg-white p-4 shadow-xl">{nav}</div>
          </div>
        ) : null}
        <main className="min-w-0 flex-1 px-4 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
